import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import axios from "axios";
import { Building2, CheckCircle, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { landingPath } from "@/lib/landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Spinner = ({ label }) => (
  <div className="flex items-center justify-center gap-2 text-gray-500">
    <Loader2 className="h-4 w-4 animate-spin text-[#001C60]" />
    <span className="text-sm">{label}</span>
  </div>
);

const AuthCallback = () => {
  const navigate = useNavigate();
  const processed = useRef(false);

  // Session 255 — a brand-new Google identity can't become an account until a
  // valid Law Firm Code ties it to a firm. The backend holds the verified
  // identity and answers `needs_firm_code`; we collect the code here instead
  // of dumping the user back on the home page.
  const [pending, setPending]       = useState(null);   // backend needs_firm_code payload
  const [fatalError, setFatalError] = useState("");
  const [firmCode, setFirmCode]     = useState("");
  const [firmStatus, setFirmStatus] = useState(null);   // null|checking|valid|invalid
  const [firmName, setFirmName]     = useState("");
  const [codeError, setCodeError]   = useState("");
  const [submitting, setSubmitting] = useState(false);
  const codeTimer = useRef(null);

  const finishLogin = useCallback(async (res) => {
    localStorage.setItem("compass_token", res.data.access_token);
    localStorage.setItem("compass_user_type", res.data.user_type);
    localStorage.setItem("compass_first_name", res.data.first_name || "");
    localStorage.setItem("compass_last_name", res.data.last_name || "");
    localStorage.setItem("compass_picture", res.data.picture || "");
    if (res.data.user_id) localStorage.setItem("compass_user_id", res.data.user_id);
    if (res.data.is_master) localStorage.setItem("compass_is_master", "true"); else localStorage.removeItem("compass_is_master");
    localStorage.removeItem("pending_google_user_type");
    localStorage.removeItem("pending_google_firm_code");
    localStorage.removeItem("compass_onboarding_done");

    // Session 222 — brand-new Google signup: mirror /auth/register's
    // navigation rule so the user lands on /onboarding EXACTLY when
    // the email/password flow would have. `has_tenant` is true when
    // the backend wired the user to a Client/Lawyer tenant; DIYs
    // don't have a firm-scoped tenant but still need onboarding.
    if (res.data.is_new && (res.data.has_tenant || res.data.user_type === "DIY")) {
      navigate("/onboarding");
      return;
    }

    // Existing users: keep the pre-existing pull-the-status behavior
    // so a returning Client/DIY whose lawyer just made an onboarding
    // wizard live still gets caught by the check.
    if (res.data.user_type === "Client" || res.data.user_type === "DIY") {
      try {
        const obRes = await axios.get(`${API}/wizards/onboarding-status`, {
          headers: { Authorization: `Bearer ${res.data.access_token}` },
        });
        if (obRes.data.needs_onboarding) {
          navigate("/onboarding");
          return;
        }
      } catch (_) { /* ignore */ }
    }
    navigate(landingPath(res.data.user_type));
  }, [navigate]);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get("session_id");
    const userType = localStorage.getItem("pending_google_user_type") || "Client";
    // Session 222 — firm_code stashed by the SIGNUP-tab Google button
    // so brand-new Google signups get the SAME tenant/lawyers setup
    // as /auth/register. Ignored server-side for existing users.
    const firmCode = localStorage.getItem("pending_google_firm_code") || "";

    if (!sessionId) {
      navigate("/");
      return;
    }

    axios
      .post(`${API}/auth/google/session`, {
        session_id: sessionId,
        user_type: userType,
        firm_code: firmCode || undefined,
      })
      .then(async (res) => {
        // Session 255 — no firm code yet: ask for it before the account exists.
        if (res.data?.needs_firm_code) {
          localStorage.removeItem("pending_google_user_type");
          localStorage.removeItem("pending_google_firm_code");
          setPending(res.data);
          setCodeError(res.data.firm_code_error || "");
          return;
        }
        await finishLogin(res);
      })
      .catch((err) => {
        console.error("Google auth callback error:", err);
        // Session 222 — surface a helpful message when the backend
        // refused the signup so the user sees the same failure they'd
        // see on /auth/register instead of a silent redirect back home.
        const detail = err?.response?.data?.detail;
        if (detail) {
          try { sessionStorage.setItem("compass_google_signup_error", detail); }
          catch (_) { /* private mode is fine */ }
        }
        localStorage.removeItem("pending_google_user_type");
        localStorage.removeItem("pending_google_firm_code");
        setFatalError(
          typeof detail === "string" && detail
            ? detail
            : "We couldn't complete your Google sign-in. Please try again."
        );
      });
  }, [navigate, finishLogin]);

  const validateCode = useCallback(async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) { setFirmStatus(null); setFirmName(""); return; }
    setFirmStatus("checking");
    try {
      const res = await axios.get(`${API}/auth/validate-firm-code`, { params: { code } });
      if (res.data.valid) {
        setFirmStatus("valid");
        setFirmName(res.data.firm_name || "");
      } else {
        setFirmStatus("invalid");
        setFirmName("");
      }
    } catch (err) {
      // Session 255 — a throttled check (429) is NOT a bad code.
      if (err?.response?.status === 429) {
        setFirmStatus(null);
        setFirmName("");
        setCodeError(err.response.data?.detail || "Too many code checks. Please wait a minute.");
        return;
      }
      setFirmStatus("invalid");
      setFirmName("");
    }
  }, []);

  const handleCodeChange = (raw) => {
    const code = (raw || "").toUpperCase();
    setFirmCode(code);
    setFirmStatus(null);
    setFirmName("");
    setCodeError("");
    clearTimeout(codeTimer.current);
    if (code.trim().length >= 2) {
      codeTimer.current = setTimeout(() => validateCode(code), 400);
    }
  };

  const handleSubmitCode = async (e) => {
    e.preventDefault();
    const code = firmCode.trim().toUpperCase();
    if (!code) { setCodeError("Please enter your law firm's code."); return; }
    setSubmitting(true);
    setCodeError("");
    try {
      const res = await axios.post(`${API}/auth/google/complete`, {
        pending_token: pending.pending_token,
        firm_code: code,
      });
      await finishLogin(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setCodeError(typeof detail === "string" && detail
        ? detail
        : "We couldn't finish creating your account. Please try again.");
      setSubmitting(false);
    }
  };

  // ── Firm-code step ───────────────────────────────────────────────────
  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div
          className="w-full max-w-md bg-white rounded-xl border border-gray-200 shadow-sm p-7 space-y-5"
          data-testid="google-firm-code-card"
        >
          <div className="space-y-2">
            <img src="/compass-logo.png" alt="Compass" className="w-12 h-12" style={{ objectFit: "contain" }} />
            <h1 className="text-xl font-semibold text-[#001C60]">One more step</h1>
            <p className="text-sm text-gray-600">
              Signed in as <strong className="text-gray-800">{pending.email}</strong>. Enter your
              law firm's code so your new account is linked to the right firm.
            </p>
          </div>

          <form onSubmit={handleSubmitCode} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="google-firm-code" className="text-sm font-medium text-gray-700">
                Law Firm Code
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  id="google-firm-code"
                  autoFocus
                  value={firmCode}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  onBlur={() => {
                    // Only fill a gap — re-validating here would flip the
                    // status to "checking" on the blur that a submit click
                    // causes, disabling the button before the click lands.
                    if (firmCode.trim() && !firmStatus) validateCode(firmCode);
                  }}
                  placeholder="e.g. KING-1234"
                  className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pl-10 pr-10 text-sm font-mono tracking-wider focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#001C60] ${
                    firmStatus === "invalid" ? "border-red-400"
                      : firmStatus === "valid" ? "border-green-400"
                        : "border-gray-300"
                  }`}
                  data-testid="google-firm-code-input"
                />
                {firmStatus === "checking" && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
                {firmStatus === "valid"    && <CheckCircle className="absolute right-3 top-2.5 h-4 w-4 text-green-500" />}
                {firmStatus === "invalid"  && <AlertCircle className="absolute right-3 top-2.5 h-4 w-4 text-red-500" />}
              </div>

              {firmStatus === "valid" && firmName && (
                <p className="flex items-center gap-1 text-xs text-green-700" data-testid="google-firm-code-valid">
                  <CheckCircle size={12} /> Linking to <strong className="ml-0.5">{firmName}</strong>
                </p>
              )}
              {firmStatus === "invalid" && !codeError && (
                <p className="text-xs text-red-700" data-testid="google-firm-code-invalid">
                  <strong>{firmCode}</strong> is not a valid firm code. Ask your law firm to confirm it.
                </p>
              )}
              {codeError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800"
                     data-testid="google-firm-code-error">
                  {codeError}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting || !firmCode.trim() || firmStatus === "invalid"}
              className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-[#001C60] px-4 text-sm font-medium text-white transition-colors hover:bg-[#002a8f] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="google-firm-code-submit"
            >
              {submitting
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating your account…</>
                : <>Finish creating my account <ArrowRight className="h-4 w-4" /></>}
            </button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full text-xs text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline"
              data-testid="google-firm-code-cancel"
            >
              Cancel and go back to sign in
            </button>
          </form>

          <p className="text-xs text-gray-400">
            Don't have a code? Ask your law firm — they can find it in their Compass settings.
          </p>
        </div>
      </div>
    );
  }

  // ── Hard failure ─────────────────────────────────────────────────────
  if (fatalError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md space-y-4 rounded-xl border border-gray-200 bg-white p-7 text-center shadow-sm"
             data-testid="auth-callback-error">
          <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
          <p className="text-sm text-gray-700">{fatalError}</p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="inline-flex h-9 items-center justify-center rounded-md bg-[#001C60] px-4 text-sm font-medium text-white hover:bg-[#002a8f]"
            data-testid="auth-callback-error-back"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── Exchanging the Google session ────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="auth-callback">
      <div className="text-center space-y-4">
        <img
          src="/compass-logo.png"
          alt="Compass"
          className="w-16 h-16 mx-auto"
          style={{ objectFit: "contain" }}
        />
        <Spinner label="Signing you in with Google..." />
      </div>
    </div>
  );
};

export default AuthCallback;
